import { useState } from 'react'
import { Formik, Form } from 'formik'
import { useNavigate } from 'react-router-dom'

import { Button } from '@components/Button'
import { SelectField } from '@components/forms/SelectField'
import { ArrowLeftIcon, ExportIcon } from '@components/Icons'
import {
  CampaignTypeEnum,
  campaignTypeLabel,
} from '@interfaces/promotionCampaign/CampaignTypeEnum'
import {
  WholeStoryDiscountTypeEnum,
  wholeStoryDiscountTypeLabel,
} from '@interfaces/promotionCampaign/WholeStoryDiscountTypeEnum'
import {
  FreeReadingTypeEnum,
  freeReadingTypeLabel,
} from '@interfaces/promotionCampaign/FreeReadingTypeEnum'

import { CampaignPreviewTable } from '../CampaignPreviewTable/CampaignPreviewTable'
import { CampaignFormProps } from './interface'
import { CampaignSettingForm } from './components/CampaignSettingForm'
import { UploadCampaignCsvField } from './components/UploadCampaignCsvField'
import { validationSchema } from './components/validationSchema'

const wholeStoryOptions = Object.values(WholeStoryDiscountTypeEnum).map(v => ({
  value: v,
  label: wholeStoryDiscountTypeLabel[v],
}))

const freeReadingOptions = Object.values(FreeReadingTypeEnum).map(v => ({
  value: v,
  label: freeReadingTypeLabel[v],
}))

function getCsvTemplatePath(
  campaignType: CampaignTypeEnum,
  wholeStoryDiscountType?: WholeStoryDiscountTypeEnum,
  freeReadingType?: FreeReadingTypeEnum
): string | undefined {
  if (campaignType === CampaignTypeEnum.PER_CHAPTER) {
    return '/templates/campaign_per_chapter.csv'
  }

  if (campaignType === CampaignTypeEnum.WHOLE_STORY) {
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.PERCENTAGE) {
      return '/templates/campaign_whole_story_percentage.csv'
    }
    if (wholeStoryDiscountType === WholeStoryDiscountTypeEnum.COIN_PRICE) {
      return '/templates/campaign_whole_story_coin_price.csv'
    }
    return undefined
  }

  // FREE_READING
  if (freeReadingType === FreeReadingTypeEnum.DAILY_LIMIT) {
    return '/templates/campaign_free_reading_daily_limit.csv'
  }
  if (freeReadingType === FreeReadingTypeEnum.DURATION_SPECIFIC) {
    return '/templates/campaign_free_reading_duration_specific.csv'
  }
  if (freeReadingType === FreeReadingTypeEnum.PERIOD_SPECIFIC) {
    return '/templates/campaign_free_reading_period_specific.csv'
  }
  return undefined
}

export function CampaignForm({
  initialValues,
  campaignType,
  title,
  handleSubmit,
  goBackHandle,
  viewMode = false,
}: CampaignFormProps) {
  const navigate = useNavigate()
  const [csvIsError, setCsvIsError] = useState(false)

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, dirty, setFieldValue, isValid, isSubmitting }) => {
        const templatePath = getCsvTemplatePath(
          campaignType,
          values.wholeStoryDiscountType,
          values.freeReadingType
        )

        function handleSubTypeChange() {
          setFieldValue('bookData', [])
          setFieldValue('csvFile', undefined)
        }

        return (
          <Form>
            <div className='flex items-center gap-x-[20px] mb-[28px]'>
              <button
                className='text-white bg-secondary px-[18px] py-[8px] rounded-[20px]'
                type='button'
                onClick={() => {
                  if (dirty && goBackHandle) {
                    goBackHandle(values)
                  } else {
                    navigate(-1)
                  }
                }}
              >
                <ArrowLeftIcon width='24' height='24' />
              </button>
              <div className='text-[24px] text-secondary font-medium font-mitr flex-1'>
                {title}
              </div>
              <span className='text-[14px] font-light text-secondary-100 bg-blue-2 px-[12px] py-[6px] rounded-full'>
                {campaignTypeLabel[campaignType]}
              </span>
            </div>

            <CampaignSettingForm viewMode={viewMode} />

            {!viewMode && campaignType === CampaignTypeEnum.WHOLE_STORY && (
              <div className='border rounded-[12px] p-[30px] mb-[20px]'>
                <SelectField
                  name='wholeStoryDiscountType'
                  label='ประเภทส่วนลด'
                  placeholder='เลือกประเภทส่วนลด'
                  options={wholeStoryOptions}
                  onChange={handleSubTypeChange}
                />
              </div>
            )}

            {!viewMode && campaignType === CampaignTypeEnum.FREE_READING && (
              <div className='border rounded-[12px] p-[30px] mb-[20px]'>
                <SelectField
                  name='freeReadingType'
                  label='ประเภทอ่านฟรี'
                  placeholder='เลือกประเภทอ่านฟรี'
                  options={freeReadingOptions}
                  onChange={handleSubTypeChange}
                />
              </div>
            )}

            {!viewMode && (
              <div className='border rounded-[12px] p-[30px] mb-[20px]'>
                <div className='flex items-center justify-between mb-[20px]'>
                  <p className='text-[18px] font-medium font-mitr text-secondary'>
                    อัปโหลดรายการหนังสือ
                  </p>
                  {templatePath ? (
                    <a
                      href={templatePath}
                      download
                      className='flex items-center gap-x-[6px] text-[14px] text-primary font-light hover:underline'
                    >
                      <ExportIcon width='16' height='16' />
                      ดาวน์โหลด CSV Template
                    </a>
                  ) : (
                    <span className='text-[14px] text-secondary-100 font-light'>
                      กรุณาเลือกประเภทย่อยก่อนดาวน์โหลด Template
                    </span>
                  )}
                </div>
                <UploadCampaignCsvField
                  campaignType={campaignType}
                  wholeStoryDiscountType={values.wholeStoryDiscountType}
                  freeReadingType={values.freeReadingType}
                  disabled={viewMode}
                  onErrorChange={setCsvIsError}
                />
              </div>
            )}

            {values.bookData && values.bookData.length > 0 && (
              <CampaignPreviewTable
                data={values.bookData}
                campaignType={campaignType}
                wholeStoryDiscountType={values.wholeStoryDiscountType}
                freeReadingType={values.freeReadingType}
              />
            )}

            {!viewMode && (
              <div className='flex justify-center gap-x-[20px] mt-[30px]'>
                <Button
                  type='button'
                  onClick={() => {
                    if (dirty && goBackHandle) {
                      goBackHandle(values)
                    } else {
                      navigate(-1)
                    }
                  }}
                  className='border border-secondary-100 text-secondary-100 text-[14px] px-[30px]'
                >
                  ยกเลิก
                </Button>
                <Button
                  type='submit'
                  className='text-white bg-primary px-[30px] text-[14px]'
                  disabled={csvIsError || !isValid || !dirty || isSubmitting}
                >
                  ส่งตรวจ
                </Button>
              </div>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
